import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { app } from '@/app';
import { Notifications } from '@/components/Notifications';
import { applyChartRanks } from '@/utils/applyChartRanks';
import { trackServerBlocks } from '@/utils/trackServerBlocks';

trackServerBlocks();

// #2934 A11Y-09 - runs outside the React tree: chart entries come from both the React app
// and server-rendered CMS blocks, so the ranks can only be resolved against the live DOM.
applyChartRanks();

const enableMocking = async () => {
  const { worker } = await import('@/mock/browser');

  // `worker.start()` returns a Promise that resolves
  // once the Service Worker is up and ready to intercept requests.
  await worker.start();
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Disable retries when debugging backend code
      ...(import.meta.env.VITE_DISABLE_QUERY_RETRIES === 'true' && {
        retry: false,
        refetchOnWindowFocus: false,
        // Keep default staleTime (0) to ensure fresh data is always fetched
        // Only disable the retry mechanism, not the caching behavior
      }),
    },
    mutations: {
      // Also disable mutation retries during debugging
      ...(import.meta.env.VITE_DISABLE_QUERY_RETRIES === 'true' && {
        retry: false,
      }),
    },
  },
});

if (app.root) {
  if (import.meta.env.DEV) {
    enableMocking();
  }

  const App = () => <app.component />;

  createRoot(app.root).render(
    <>
      <Notifications />
      <StrictMode>
        <QueryClientProvider client={queryClient}>
          <App />
        </QueryClientProvider>
      </StrictMode>
    </>,
  );
}
